/**
 * Manual assign modal — pick any provider in the system for a matching-stage client.
 *
 * Searchable dropdown of providers labelled "{provider name} - {practice name}".
 * The submit action is a stub for now (mirrors MatchingModal's assign); wire the
 * real assignment write when the matching/assignment write path lands.
 */

import { Alert, Button, Group, Modal, Paper, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import {
  formatPractitionerStaffLabel,
  getPractitionerOrganizationRef,
  getRole,
} from '@unify/fhir-model/resources/practitioner';
import { useMedplum } from '@medplum/react-hooks';
import type { Organization, Practitioner } from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';
import { PROVIDER_ROLES } from '../../users/types/rbac';

interface ProviderOption {
  value: string;
  label: string;
}

// TODO: replace with real provider-vs-client compatibility checks (state license,
// payer enrollment, language) and live caseload count once sourced from FHIR.
const DUMMY_PROVIDER_DETAILS = {
  caseloadCount: 18,
  stateOk: true,
  insuranceOk: true,
  languageOk: false,
};

/** Active providers/supervisors in the system, labelled "{name} - {practice}". */
async function loadProviderOptions(medplum: ReturnType<typeof useMedplum>): Promise<ProviderOption[]> {
  const practitioners = (await medplum.searchResources('Practitioner', {
    _count: '500',
  })) as Practitioner[];

  const providers = practitioners.filter((p) => {
    if (!p.id || p.active === false) return false;
    const role = getRole(p);
    // Missing role defaults to provider (matches the admin directory mapping).
    return role == null || PROVIDER_ROLES.includes(role);
  });

  // Resolve practice names: prefer the org-ref display, fall back to a single
  // Organization lookup for refs that did not carry one.
  const orgIdsNeedingName = new Set<string>();
  for (const p of providers) {
    const ref = getPractitionerOrganizationRef(p);
    if (ref?.reference && !ref.display) {
      orgIdsNeedingName.add(ref.reference.replace('Organization/', ''));
    }
  }

  const orgNameById = new Map<string, string>();
  if (orgIdsNeedingName.size > 0) {
    const orgs = (await medplum.searchResources('Organization', {
      _id: [...orgIdsNeedingName].join(','),
      _count: String(orgIdsNeedingName.size),
    })) as Organization[];
    for (const o of orgs) {
      if (o.id && o.name) orgNameById.set(o.id, o.name);
    }
  }

  return providers
    .map((p) => {
      const name = formatPractitionerStaffLabel(p) || p.name?.[0]?.text?.trim() || 'Provider';
      const ref = getPractitionerOrganizationRef(p);
      const practice =
        ref?.display ?? (ref?.reference ? orgNameById.get(ref.reference.replace('Organization/', '')) : undefined);
      return { value: p.id as string, label: practice ? `${name} - ${practice}` : name };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

function DetailRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

function DetailCheck({ label, ok }: { label: string; ok: boolean | null }): JSX.Element {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {label}
      </Text>
      {ok == null ? null : ok ? (
        <IconCircleCheck size={18} color="var(--mantine-color-teal-6)" />
      ) : (
        <IconCircleX size={18} color="var(--mantine-color-red-6)" />
      )}
    </Group>
  );
}

export interface ManualAssignModalProps {
  open: boolean;
  onClose: () => void;
  clientName: string;
  clientId?: string;
}

export function ManualAssignModal({
  open,
  onClose,
  clientName,
}: ManualAssignModalProps): JSX.Element {
  const medplum = useMedplum();
  const [options, setOptions] = useState<ProviderOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(null);
    void (async () => {
      try {
        const result = await loadProviderOptions(medplum);
        if (!cancelled) setOptions(result);
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
          setError(e instanceof Error ? e.message : 'Failed to load providers.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, medplum]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === selected)?.label ?? '',
    [options, selected],
  );

  // TODO: replace with the real assignment write (set EpisodeOfCare.careManager
  // and/or append the referral Task output) once the assignment path lands.
  const handleSubmit = (): void => {
    notifications.show({
      color: 'teal',
      title: 'Client assigned',
      message: `${clientName || 'Client'} assigned to ${selectedLabel}.`,
    });
    onClose();
  };

  return (
    <Modal opened={open} onClose={onClose} title="Manual assign" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Assign {clientName || 'this client'} directly to a provider.
        </Text>

        {error ? (
          <Alert color="red" title="Could not load providers">
            {error}
          </Alert>
        ) : (
          <Select
            label="Provider"
            placeholder={loading ? 'Loading providers…' : 'Search providers…'}
            data={options}
            value={selected}
            onChange={setSelected}
            searchable
            disabled={loading}
            nothingFoundMessage="No providers found"
            comboboxProps={{ withinPortal: true }}
          />
        )}

        <Paper withBorder radius="md" p="md" bg="gray.0">
          <Stack gap="sm">
            <DetailRow
              label="Caseload"
              value={selected ? String(DUMMY_PROVIDER_DETAILS.caseloadCount) : ''}
            />
            <DetailCheck label="State" ok={selected ? DUMMY_PROVIDER_DETAILS.stateOk : null} />
            <DetailCheck
              label="Insurance"
              ok={selected ? DUMMY_PROVIDER_DETAILS.insuranceOk : null}
            />
            <DetailCheck label="Language" ok={selected ? DUMMY_PROVIDER_DETAILS.languageOk : null} />
            <Text size="xs" c="dimmed" fs="italic">
              Dummy data — not yet wired to provider records.
            </Text>
          </Stack>
        </Paper>

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selected}>
            Submit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
